import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ToastType } from '@d-constants';
import { ToastService } from '@d-services';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  FileSystemFileEntry,
  NgxFileDropEntry,
  NgxFileDropModule,
} from 'ngx-file-drop';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-files-uploader',
  templateUrl: './files-uploader.component.html',
  styleUrls: ['./files-uploader.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NgxFileDropModule,
    ButtonModule,
    ToastModule,
    TooltipModule,
    TranslateModule,
  ],
  providers: [MessageService],
})
export class FilesUploaderComponent implements OnChanges {
  @Input() selectedFiles: any[] = [];
  @Input() disabled = false;
  @Input() disabledFile = false;
  @Input() maxFiles = 10;
  @Input() maxFileSize = 20; //MB
  @Input() acceptType = ['.pdf', '.docx', '.xlsx', '.xml'];
  @Input() containerClass = '';
  @Output() onFileSelected: EventEmitter<any> = new EventEmitter();
  @Input() multipleSelect = true;
  @Input() silentErrors: string[] = [];
  TOAST_MSG_LIFE_TIME = 4000; //miliseconds
  files = [];
  invalidChars = /[\\/:*?"<>|]/;

  @Output() errorType: EventEmitter<any> = new EventEmitter();
  constructor(
    private messageService: MessageService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private _sanitizer: DomSanitizer,
  ) { }

  ngOnChanges(changes): void {
    if (changes.selectedFiles && changes.selectedFiles.currentValue.length) {
      this.files = changes.selectedFiles.currentValue.map((item) => {
        let isFile = item instanceof File;
        if (isFile) {
          item.viewAttachmentUrl = this._sanitizer.bypassSecurityTrustUrl(
            URL.createObjectURL(item),
          );
          return item;
        } else {
          return {
            ...item,
            viewAttachmentUrl: item.viewAttachmentUrl || item.attachmentUrl,
          };
        }
      }); // re-assign value to map diffrent structure from BE
    }
  }

  public onFileDrop(files: NgxFileDropEntry[]) {
    if (!this.isValidMaxFiles(files.length)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          'Total files selected cannot exceed ' + this.maxFiles + ' files!',
        life: this.TOAST_MSG_LIFE_TIME,
      });
      return;
    }
    this.getSelectedFiles(files);
  }

  isValidMaxFiles(selectingFiles: number) {
    return selectingFiles + this.selectedFiles.length <= this.maxFiles;
  }

  isValidMaxSize(fileSize: number) {
    const fileSizeInMb = this.formatBytesToMb(fileSize);
    if (fileSizeInMb === '0 Bytes') {
      return false;
    }
    return (this.formatBytesToMb(fileSize) as number) <= this.maxFileSize;
  }

  isValidFileType(fileName: string) {
    if (!fileName || fileName.trim() === '')
      return false;

    if (this.invalidChars.test(fileName))
      return false;

    const parts = fileName.split('.');

    if (parts.length !== 2)
      return false;

    var fileExtension = this.getFileExtension(fileName) ?? '';
    return this.acceptType.includes(fileExtension);
  }

  toastError(
    message: string,
    opts: { messageParams?: any; emitValue?: any } = {},
  ) {
    if (!this.silentErrors.includes(message)) {
      this.toastService.addSingle({
        type: ToastType.ERROR,
        message,
        messageParams: opts.messageParams,
        life: this.TOAST_MSG_LIFE_TIME,
      });
    }
    this.errorType.emit({ type: message, value: opts.emitValue ?? '' });
  }

  getSelectedFiles(files: NgxFileDropEntry[]) {
    for (const droppedFile of files) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: any) => {
          this.handleFile(file);
        });
      }
    }
  }

  handleFile(file: any) {
    if (this.isValidMaxSize(file.size) && this.isValidFileType(file.name)) {
      this.files.push({
        ...file,
        icon: this.iconMapper(file.name),
        name: file.name,
        viewAttachmentUrl: this._sanitizer.bypassSecurityTrustUrl(
          URL.createObjectURL(file),
        ),
      });
      this.selectedFiles.push(file);
      this.onFileSelected.emit(this.selectedFiles);
    } else if (!this.isValidFileType(file.name)) {
      this.toastError('INVALID_FILE_TYPE');
    } else if (!this.isValidMaxSize(file.size)) {
      this.toastError('INVALID_FILE_SIZE', {
        messageParams: { size: this.maxFileSize },
        emitValue: this.formatBytesToMb(file.size),
      });
    }
  }

  iconMapper(fileName: string) {
    var fileExtension = this.getFileExtension(fileName);
    if (fileExtension === '.docx') {
      return 'pi pi-file';
    }
    if (fileExtension === '.xlsx') {
      return 'pi pi-file-excel';
    }
    if (fileExtension === '.pdf') {
      return 'pi pi-file-pdf';
    }
    return 'pi pi-file';
  }

  getFileExtension(fileName: string) {
    return '.' + fileName.split('.').pop()?.toLocaleLowerCase();
  }

  formatBytesToMb(bytes: number, decimals = 3) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    return parseFloat((bytes / Math.pow(k, 2)).toFixed(dm));
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
    this.selectedFiles.splice(index, 1);
    this.onFileSelected.emit(this.selectedFiles);
  }

  getFileFormatDescription() {
    return `${this.translateService.instant(
      'FILE_FORMAT',
    )}: ${this.acceptType.join(', ')} (${this.translateService.instant(
      'MAX',
    )}. ${this.maxFileSize} MB)`;
  }
}
